<template>
    <h2 v-if="!!title && title.length > 0">{{ title }}</h2>
    <div class="">
        <div class="mb-3" v-show="!showExtraFields" ref="autocompleteWrapper">
            <label v-if="!!addressLabel && addressLabel.length > 0" class="form-label" :for="autocompleteInput + '-search'">{{ addressLabel}}</label>
            <div class="autocomplete-wrapper" style="position: relative;">
                <input
                    class="form-control"
                    :class="{ 'is-invalid': !!searchError }"
                    type="text"
                    @input.prevent="handleDebouncedInput"
                    :value="fullAddress"
                    :placeholder="getTranslatedText('__type_to_search__', 'fields.placeholders')"
                    :autocomplete="autocompleteInput"
                    :id="autocompleteInput + '-search'"
                    :aria-invalid="searchError ? 'true' : null"
                    :aria-describedby="searchError ? autocompleteInput + '-search-error' : null"
                />
                <div v-if="showLoadingAnimation && fullAddress.length" class="searching-spinner"></div>
                <ul v-if="predictions.length" class="form-control dropdown-list" style="position: absolute; z-index: 10; background: white; width: 100%; border: 1px solid #ccc; ">
                    <li
                        v-for="prediction in predictions"
                        :key="prediction.place_id"
                        @click="selectPrediction(prediction)"
                        style="padding: 8px; cursor: pointer;"
                    >
                        {{ prediction.description }}
                    </li>
                </ul>
            </div>
            <!-- The address-level message ("Enter your address") belongs to the search box only:
                 once the six fields are open, each part says what it is missing instead. -->
            <LcFieldErrors v-if="searchError" :id="autocompleteInput + '-search-error'" :errors="[searchError]" />
        </div>

        <div v-if="showExtraFields" class="">

            <div v-if="isStepReadOnly">
                <span v-if="!!addressLabel && addressLabel.length > 0">&#x2022; {{ addressLabel }}</span>
                <p class="text-muted" v-html="getReadOnlyFullAddress"></p>
            </div>

            <div v-else>
                <label v-if="!!addressLabel && addressLabel.length > 0" class="form-label text-muted mb-3">
                    {{ addressLabel }}
                </label>
                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-cn'">{{ getTranslatedText('__address_civic_number__', 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <input
                            class="form-control"
                            :class="{ 'is-invalid': !!errors('streetNumber') }"
                            type="text"
                            @input="onPartInput('streetNumber', $event.target.value)"
                            @blur="onPartBlur('streetNumber', $event.target.value)"
                            :value="streetNumber"
                            :placeholder="getTranslatedText('__address_number__', 'fields.placeholders')"
                            :autocomplete="autocompleteInput"
                            :name="autocompleteInput + '-cn'"
                            :id="autocompleteInput + '-cn'"
                            :aria-invalid="errors('streetNumber') ? 'true' : null"
                            :aria-describedby="errors('streetNumber') ? autocompleteInput + '-cn-error' : null"
                        />
                        <LcFieldErrors v-if="errors('streetNumber')" :id="autocompleteInput + '-cn-error'" :errors="[errors('streetNumber')]" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-s'">{{ getTranslatedText('__address_street__', 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <input
                            class="form-control"
                            :class="{ 'is-invalid': !!errors('street') }"
                            type="text"
                            @input="onPartInput('street', $event.target.value)"
                            @blur="onPartBlur('street', $event.target.value)"
                            :value="street"
                            :placeholder="getTranslatedText('__address_street__', 'fields.placeholders')"
                            :autocomplete="autocompleteInput"
                            :name="autocompleteInput + '-s'"
                            :id="autocompleteInput + '-s'"
                            :aria-invalid="errors('street') ? 'true' : null"
                            :aria-describedby="errors('street') ? autocompleteInput + '-s-error' : null"
                        />
                        <LcFieldErrors v-if="errors('street')" :id="autocompleteInput + '-s-error'" :errors="[errors('street')]" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-un'">{{ getTranslatedText('__address_unit_number__', 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <input
                            class="form-control"
                            :class="{ 'is-invalid': !!errors('unitNumber') }"
                            type="text"
                            @input="onPartInput('unitNumber', $event.target.value)"
                            @blur="onPartBlur('unitNumber', $event.target.value)"
                            :value="unitNumber"
                            :placeholder="getTranslatedText('__address_unit__', 'fields.placeholders')"
                            :autocomplete="autocompleteInput"
                            :name="autocompleteInput + '-un'"
                            :id="autocompleteInput + '-un'"
                            :aria-invalid="errors('unitNumber') ? 'true' : null"
                            :aria-describedby="errors('unitNumber') ? autocompleteInput + '-un-error' : null"
                        />
                        <LcFieldErrors v-if="errors('unitNumber')" :id="autocompleteInput + '-un-error'" :errors="[errors('unitNumber')]" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-p'">{{ getTranslatedText(getPostalCodeLabel, 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <input
                            class="form-control"
                            :class="{ 'is-invalid': !!errors('postalCode') }"
                            type="text"
                            @input="onPartInput('postalCode', $event.target.value)"
                            @blur="onPartBlur('postalCode', $event.target.value)"
                            :value="postalCode"
                            :placeholder="getPostalCodePlaceholder"
                            :minlength="getPostalCodeMinLength"
                            :maxlength="getPostalCodeMaxLength"
                            :autocomplete="autocompleteInput"
                            :name="autocompleteInput + '-p'"
                            :id="autocompleteInput + '-p'"
                            :aria-invalid="errors('postalCode') ? 'true' : null"
                            :aria-describedby="errors('postalCode') ? autocompleteInput + '-p-error' : null"
                        />
                        <LcFieldErrors v-if="errors('postalCode')" :id="autocompleteInput + '-p-error'" :errors="[errors('postalCode')]" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-c'">{{ getTranslatedText('__address_city__', 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <input
                            class="form-control"
                            :class="{ 'is-invalid': !!errors('city') }"
                            type="text"
                            @input="onPartInput('city', $event.target.value)"
                            @blur="onPartBlur('city', $event.target.value)"
                            :value="city"
                            :placeholder="getTranslatedText('__address_city__', 'fields.placeholders')"
                            :autocomplete="autocompleteInput"
                            :name="autocompleteInput + '-c'"
                            :id="autocompleteInput + '-c'"
                            :aria-invalid="errors('city') ? 'true' : null"
                            :aria-describedby="errors('city') ? autocompleteInput + '-c-error' : null"
                        />
                        <LcFieldErrors v-if="errors('city')" :id="autocompleteInput + '-c-error'" :errors="[errors('city')]" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label" :for="autocompleteInput + '-pr'">{{ getTranslatedText(getProvinceLabel, 'fields.labels') }}</label>
                    <div class="mb-3 input-group has-validation">
                        <SelectField
                            :class="{ 'is-invalid': !!errors('province') }"
                            :choices="getProvincesOrStates"
                            @input="onPartPick('province', $event)"
                            :value="province"
                            :name="autocompleteInput + '-pr'"
                            :id="autocompleteInput + '-pr'"
                            :aria-invalid="errors('province') ? 'true' : null"
                            :aria-describedby="errors('province') ? autocompleteInput + '-pr-error' : null"
                        />
                        <LcFieldErrors v-if="errors('province')" :id="autocompleteInput + '-pr-error'" :errors="[errors('province')]" />
                    </div>
                </div>

            </div>
        </div>
    </div>

    <button
        v-if="!showExtraFields"
        type="button"
        class="btn btn-small btn-outline-primary"
        @click.prevent.once="showAllFields = true"
    >
        {{ getTranslatedText('__address_not_listed__', 'buttons') }}
    </button>
</template>

<script setup>
import { inject, computed, ref, reactive, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import SelectField from '@/components/form/inputs/SelectField.vue';
import LcFieldErrors from '@/components/form/LcFieldErrors.vue';
import { isEmptyValue } from '@/form/helpers/validation-display.js';
import { CANADIAN_PROVINCES, US_STATES } from '@/constants/geographies.js';
import { CA_POSTAL_REGEX, US_ZIP_REGEX } from '@/form/helpers/field-validations.js';

const props = defineProps({
    title: {
        type: String,
        default: "",
    },
    addressLabel: {
        type: String,
        default: "",
    },
    fullAddress: {
        type: String,
        default: "",
    },
    street: {
        type: String,
        default: "",
    },
    streetNumber: {
        type: String,
        default: "",
    },
    unitNumber: {
        type: String,
        default: "",
    },
    postalCode: {
        type: String,
        default: "",
    },
    city: {
        type: String,
        default: "",
    },
    province: {
        type: String,
        default: "",
    },
    fieldValidator: {
        type: Object,
    },
    isStepReadOnly: {
        type: Boolean,
        required: false,
        default: false,
    },
    hasPrepopAddress: {
        type: Boolean,
        required: false,
        default: false,
    },
    country: {
        type: String,
        required: false,
        default: 'CA',
    },
    // the page's language, for the suggestions list
    lang: {
        type: String,
        required: false,
        default: 'en',
    },
});

const emit = defineEmits([
    'update:fullAddress',
    'update:street',
    'update:streetNumber',
    'update:unitNumber',
    'update:postalCode',
    'update:city',
    'update:province',
]);

const { getTranslatedText } = inject('app');

// --- Refs ---

const autocompleteWrapper = ref(null);
const showAllFields = ref(false);
const addressChanged = ref(false);
const predictions = ref([]);
const showLoadingAnimation = ref(false);
const googleAutocompleteService = ref(null);
const googlePlaceService = ref(null);

let debounceTimeout = null;
const autocompleteInput = "lcappformfield_" + Math.random();
const customValidationMessages = {};

// The address is validated as a whole on every edit (Address.vue), so invalidAddressFields
// always lists every incomplete part. These sets decide which of those the person sees, by the
// same rules as single fields (validation-display.js): a part's error appears once it was typed
// in and left non-empty, after a suggestion is picked, or after Next; never while typing; and it
// is dropped as soon as the part is valid, not to return until the next blur.
const dirtyParts = reactive(new Set());
const shownParts = reactive(new Set());

// --- Computed ---

const showExtraFields = computed(() => {
    return props.hasPrepopAddress || addressChanged.value || showAllFields.value;
});

const getProvincesOrStates = computed(() => {
    return props.country === 'US' ? US_STATES : CANADIAN_PROVINCES;
});

const getProvinceLabel = computed(() => {
    return props.country === 'US' ? '__address_state__' : '__address_province__';
});

const getPostalCodeLabel = computed(() => {
    return props.country === 'US' ? '__address_zip__' : '__address_postal_code__';
});

const getPostalCodePlaceholder = computed(() => {
    return props.country === 'US' ? '12345' : 'A1A1A1';
});

const getPostalCodeMinLength = computed(() => {
    return props.country === 'US' ? 5 : 6;
});

const getPostalCodeMaxLength = computed(() => {
    return props.country === 'US' ? 10 : 7;
});

const getReadOnlyFullAddress = computed(() => {
    let fullAddress = '';
    if (props.unitNumber) {
        fullAddress += (props.unitNumber ?? '') + '-';
    }
    if (props.streetNumber) {
        fullAddress += (props.streetNumber ?? '') + ' ';
    }
    if (props.street) {
        fullAddress += (props.street ?? '') + '\r\n';
    }
    if (props.city) {
        fullAddress += (props.city ?? '') + ', ';
    }
    if (props.province) {
        fullAddress += (props.province ?? '');
    }
    if (props.postalCode) {
        fullAddress += '\r\n' + (props.postalCode ?? '');
    }
    return fullAddress;
});

// --- Methods ---

function invalidParts() {
    return props.fieldValidator?.invalidAddressFields ?? [];
}

// The message LcField would show for the whole address. It only renders in the search-box view
// (the block it sits in is v-show'd off with the six fields), where there are no part errors.
const searchError = computed(() => {
    const messages = props.fieldValidator?.errorMessage;
    return (!showExtraFields.value && Array.isArray(messages) && messages.length > 0) ? messages[0] : null;
});

function errors(field) {
    return shownParts.has(field) && invalidParts().includes(field) ?
        getCustomErrorMessage(field) :
        false;
}

function getCustomErrorMessage(field) {
    const requiredMessage = field === 'province' ? '__required_choice__' : '__required_field__';
    return customValidationMessages[field] ?? getTranslatedText(requiredMessage, 'validations');
}

function onPartInput(part, value) {
    dirtyParts.add(part);
    emit(`update:${part}`, value);
}

// a picked province is a finished answer, like a radio choice
function onPartPick(part, value) {
    dirtyParts.add(part);
    shownParts.add(part);
    emit(`update:${part}`, value);
}

// reads the input itself: the prop only catches up on the next render
function onPartBlur(part, value = props[part]) {
    if (dirtyParts.has(part) && !isEmptyValue(value)) {
        shownParts.add(part);
    }
}

function showAllInvalidParts() {
    invalidParts().forEach(part => shownParts.add(part));
}

// a part that became valid stops showing, and won't show again while the person types
watch(() => [...invalidParts()], (invalid) => {
    shownParts.forEach(part => {
        if (!invalid.includes(part)) {
            shownParts.delete(part);
        }
    });
});

// Next was pressed (useFormValidation.validateForm bumps revealCount on each press). Only parts
// that were on screen at the time: six fields opened after a Next from the search box start
// clean, since the person never saw them when they pressed it.
watch(() => props.fieldValidator?.revealCount, (count) => {
    if (count > 0 && showExtraFields.value) {
        showAllInvalidParts();
    }
});

function isPostalShapeValid(value) {
    if (typeof value !== 'string' || !value.length) {
        return false;
    }
    const regex = props.country === 'US' ? US_ZIP_REGEX : CA_POSTAL_REGEX;
    return regex.test(value);
}

async function selectPrediction(prediction) {
    predictions.value = [];
    addressChanged.value = true;

    const placeApi = new googlePlaceService.value({
        id: prediction.place_id,
    });

    try {
        await placeApi.fetchFields({
            fields: ["addressComponents"],
        });
        const addressComponents = placeApi.addressComponents;
        if (!addressComponents || !addressComponents.length) {
            console.error('No address components found');
            return;
        }

        const mapping = {
            street_number: "update:streetNumber",
            route: "update:street",
            locality: "update:city",
            postal_code: "update:postalCode",
            administrative_area_level_1: "update:province",
        };

        const mappingShortVersion = [
            "postal_code",
            "administrative_area_level_1",
        ];

        for (const type in mapping) {
            emit(mapping[type], "");
        }

        addressComponents.forEach((component) => {
            component.types.forEach((type) => {
                if (mapping.hasOwnProperty(type)) {
                    const fieldValue = mappingShortVersion.includes(type) ? component.shortText : component.longText;
                    // Google Places can return a malformed postal_code (e.g. a partial LDU like "N8H 3V"
                    // for route-level matches). Drop those so the field stays empty and the user is
                    // forced to type a valid code — rather than shipping garbage downstream.
                    if (type === 'postal_code' && !isPostalShapeValid(fieldValue)) {
                        return;
                    }
                    emit(mapping[type], fieldValue);
                }
            });
        });

        // A suggestion can leave parts empty (e.g. a malformed postal code dropped above); point
        // them out now, since the person has nothing left to type before Next. Address.vue
        // re-validates on the next flush, so wait for it before reading invalidAddressFields.
        await nextTick();
        showAllInvalidParts();
    } catch (error) {
        console.error("Error fetching address components:", error);
    }
}

function handleDebouncedInput(event) {
    clearTimeout(debounceTimeout);
    showLoadingAnimation.value = true;
    const userInput = event.target.value;
    emit('update:fullAddress', userInput);
    debounceTimeout = setTimeout(() => {
        if (!userInput.trim()) {
            predictions.value = [];
            return;
        }

        googleAutocompleteService.value.getPlacePredictions(
            {
                input: userInput,
                types: ["address"],
                componentRestrictions: { country: props.country.toLowerCase() },
                // Google answers in the browser's language otherwise: English suggestions on a
                // French page (pq). Only the list; the picked address's parts come back as before.
                language: props.lang,
            },
            (results, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    predictions.value = results;
                } else {
                    console.warn('Error fetching predictions:', status);
                    showAllFields.value = true;
                    predictions.value = [];
                }

                showLoadingAnimation.value = false;
            }
        );
    }, 1000);
}

function handleClickOutside(event) {
    if (autocompleteWrapper.value && !autocompleteWrapper.value.contains(event.target) && predictions.value.length) {
        predictions.value = [];
        addressChanged.value = true;
        showAllFields.value = true;
    }
}

// --- Lifecycle ---

onMounted(async () => {
    customValidationMessages['postalCode'] = getTranslatedText(props.country === 'US' ? '__zip_code_format__' : '__postal_code_format__', 'validations');

    if (props.street) {
        showAllFields.value = true;
    }

    document.addEventListener('click', handleClickOutside);

    window.gm_authFailure = () => {
        console.error('gm_authFailure');
        showAllFields.value = true;
    };

    if (!!props.hasPrepopAddress) {
        showAllFields.value = true;
    } else {
        const loader = new Loader({
            apiKey: process.env.MIX_AUTOCOMPLETE_API_KEY,
            version: "weekly",
        });

        await loader.load();

        let googlePlacesLibrary = null;
        try {
            googlePlacesLibrary = await google.maps.importLibrary('places');
        } catch (error) {
            console.error('Error loading Google Places Library:', error);
            showAllFields.value = true;
        }

        if (googlePlacesLibrary) {
            googleAutocompleteService.value = new googlePlacesLibrary.AutocompleteService();
            googlePlaceService.value = googlePlacesLibrary.Place;
        } else {
            console.log('Google Places Library failed to load');
            showAllFields.value = true;
        }
    }
});

onBeforeUnmount(() => {
    if (googleAutocompleteService.value || googlePlaceService.value) {
        if (googleAutocompleteService.value) {
            google.maps.event.clearInstanceListeners(googleAutocompleteService.value);
            googleAutocompleteService.value = null;
        }

        if (googlePlaceService.value) {
            google.maps.event.clearInstanceListeners(googlePlaceService.value);
            googlePlaceService.value = null;
        }
    }

    if (debounceTimeout) {
        clearTimeout(debounceTimeout);
    }

    document.removeEventListener('click', handleClickOutside);

    window.gm_authFailure = undefined;
});
</script>

<style scoped>
.dropdown-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 200px;
    overflow-y: auto;
}

.dropdown-list li.active {
    background-color: #eee;
}

.searching-spinner {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    width: 1rem;
    height: 1rem;
    transform: translateY(-50%);
    border: 2px solid #ccc;
    border-top-color: #333;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to { transform: translateY(-50%) rotate(360deg); }
}
</style>
